import { Button } from "@/components/core/buttons/button";
import { Checkbox } from "@/components/core/forms/checkbox";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { useModal } from "@/components/core/modals/use-modal";
import { Content } from "@/components/layouts/content";
import {
	invoicePrintTemplateQueryOptions,
	useInvoicePrintTemplateActivateMutation,
	useInvoicePrintTemplateBackgroundMutation,
	useInvoicePrintTemplateDeleteMutation,
	useInvoicePrintTemplateUpdateMutation,
} from "@/features/dashboard/api/queries/invoice-print-template-query";
import { invoiceRequestsQueryOptions } from "@/features/dashboard/api/queries/invoice-request-query";
import {
	FIELD_KEYS,
	FIELD_LABELS,
	type FieldAlign,
	type FieldKey,
	type FieldSetting,
	ITEM_COLUMN_KEYS,
	ITEM_COLUMN_LABELS,
	type ItemColumnKey,
	type ItemColumnSetting,
	type ItemsSetting,
	type Orientation,
	type PageSize,
	type PrintTemplateSettings,
	type SignatorySetting,
	type SummarySetting,
} from "@/features/dashboard/types/invoice-print-template";
import { IconArrowLeft, IconCheck, IconFileTypePdf, IconPlus, IconTrash, IconUpload } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

const PAGE_SIZE_OPTIONS = [
	{ value: "a4", label: "A4" },
	{ value: "letter", label: "Letter" },
	{ value: "legal", label: "Legal" },
];

const ORIENTATION_OPTIONS = [
	{ value: "portrait", label: "Portrait" },
	{ value: "landscape", label: "Landscape" },
];

const ALIGN_OPTIONS: FieldAlign[] = ["left", "center", "right"];

const SAMPLE_FIELD_TEXT: Record<FieldKey, string> = {
	client_name: "Acme Corporation",
	reference_no: "REF-0001",
	tin_no: "000-000-000-000",
	invoice_date: "January 1, 2026",
	address: "123 Sample Street, Makati City",
	terms: "Due on receipt",
};

// Mirrors reports/invoice-request.blade.php: table cells wrap and grow the
// row to fit (word-wrap: break-word; overflow: visible), pushing later rows
// down rather than clipping. A long value here is deliberate, to preview that.
const SAMPLE_ITEMS: Array<Record<ItemColumnKey, string>> = [
	{
		quantity: "2",
		uom: "PCS",
		description: "Heavy-duty industrial shelving unit with adjustable shelves and reinforced steel frame",
		unit_price: "1,500.00",
		amount: "3,000.00",
	},
	{
		quantity: "1",
		uom: "LOT",
		description: "Installation fee",
		unit_price: "500.00",
		amount: "500.00",
	},
	{
		quantity: "5",
		uom: "PCS",
		description: "Safety helmets",
		unit_price: "250.00",
		amount: "1,250.00",
	},
];

type EditableTemplate = {
	name: string;
	page_size: PageSize;
	orientation: Orientation;
	settings: PrintTemplateSettings;
};

export const Route = createFileRoute("/_dashboard/invoice-print-templates_/$templateId")({
	component: Page,
});

function Page() {
	const { templateId } = Route.useParams();
	const navigate = useNavigate();
	const modal = useModal();

	const templateQuery = useQuery(invoicePrintTemplateQueryOptions(templateId));
	const invoiceRequestsQuery = useQuery(invoiceRequestsQueryOptions({ per_page: 20 }));
	const updateMutation = useInvoicePrintTemplateUpdateMutation(templateId);
	const activateMutation = useInvoicePrintTemplateActivateMutation();
	const deleteMutation = useInvoicePrintTemplateDeleteMutation();
	const backgroundMutation = useInvoicePrintTemplateBackgroundMutation(templateId);

	const [draft, setDraft] = useState<EditableTemplate | null>(null);
	const [previewInvoiceRequestId, setPreviewInvoiceRequestId] = useState<string | null>(null);
	const initialized = useRef(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (initialized.current || !templateQuery.data) return;
		initialized.current = true;
		const t = templateQuery.data;
		setDraft({ name: t.name, page_size: t.page_size, orientation: t.orientation, settings: t.settings });
	}, [templateQuery.data]);

	const scale = 480 / (draft?.settings.page.width || 794);

	const updateField = (key: FieldKey, patch: Partial<FieldSetting>) => {
		setDraft((prev) => {
			if (!prev) return prev;
			const current = prev.settings.fields[key];
			return { ...prev, settings: { ...prev.settings, fields: { ...prev.settings.fields, [key]: { ...current, ...patch } } } };
		});
	};

	const updatePage = (patch: Partial<PrintTemplateSettings["page"]>) => {
		setDraft((prev) => (prev ? { ...prev, settings: { ...prev.settings, page: { ...prev.settings.page, ...patch } } } : prev));
	};

	const updateItems = (patch: Partial<ItemsSetting>) => {
		setDraft((prev) => (prev ? { ...prev, settings: { ...prev.settings, items: { ...prev.settings.items, ...patch } } } : prev));
	};

	const updateItemColumn = (key: ItemColumnKey, patch: Partial<ItemColumnSetting>) => {
		setDraft((prev) => {
			if (!prev) return prev;
			const current = prev.settings.items.columns[key];
			return {
				...prev,
				settings: { ...prev.settings, items: { ...prev.settings.items, columns: { ...prev.settings.items.columns, [key]: { ...current, ...patch } } } },
			};
		});
	};

	const updateSummary = (patch: Partial<SummarySetting>) => {
		setDraft((prev) => (prev ? { ...prev, settings: { ...prev.settings, summary: { ...prev.settings.summary, ...patch } } } : prev));
	};

	const updateSignatory = (index: number, patch: Partial<SignatorySetting>) => {
		setDraft((prev) => {
			if (!prev) return prev;
			const signatories = prev.settings.signatories.map((s, i) => (i === index ? { ...s, ...patch } : s));
			return { ...prev, settings: { ...prev.settings, signatories } };
		});
	};

	const addSignatory = () => {
		setDraft((prev) =>
			prev
				? {
						...prev,
						settings: {
							...prev.settings,
							signatories: [...prev.settings.signatories, { name: "New Signatory", x: 40, y: 878, width: 178, font_size: 9.5 }],
						},
					}
				: prev,
		);
	};

	const removeSignatory = (index: number) => {
		setDraft((prev) => (prev ? { ...prev, settings: { ...prev.settings, signatories: prev.settings.signatories.filter((_, i) => i !== index) } } : prev));
	};

	const handleSave = async () => {
		if (!draft) return;
		await updateMutation.mutateAsync(draft);
		toast.success("Template saved");
	};

	const handleActivate = async () => {
		await activateMutation.mutateAsync(templateId);
		toast.success("Template activated");
	};

	const handleDelete = () => {
		modal.openModal({
			type: "confirm",
			variant: "red",
			title: "Delete this template?",
			description: "This cannot be undone.",
			onConfirm: async () => {
				await deleteMutation.mutateAsync(templateId);
				toast.success("Template deleted");
				navigate({ to: "/invoice-print-templates" });
			},
		});
	};

	const handleBackgroundChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		await backgroundMutation.mutateAsync(file);
		toast.success("Background updated");
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const handlePreview = () => {
		const query = previewInvoiceRequestId ? `?invoice_request_id=${previewInvoiceRequestId}` : "";
		window.open(`/api/invoice-print-templates/${templateId}/preview${query}`, "_blank");
	};

	if (templateQuery.isLoading || !draft) {
		return (
			<Content pageName="Invoice Print Template">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading template...</p>
					</div>
				</div>
			</Content>
		);
	}

	const template = templateQuery.data;
	if (!template) {
		return (
			<Content pageName="Invoice Print Template">
				<div className="flex items-center justify-center h-64">
					<p className="text-gray-600">Template not found</p>
				</div>
			</Content>
		);
	}

	const invoiceRequestOptions = (invoiceRequestsQuery.data?.data ?? []).map((r) => ({
		value: r.id ?? "",
		label: `${r.client_name || "Unnamed"} — ${r.id?.slice(0, 8)}`,
	}));

	// Actual generated PDF for the last-saved settings — an iframe of this endpoint is the
	// only byte-accurate preview, since the live draft canvas below is an HTML/CSS
	// approximation and can drift from dompdf's own font metrics and text layout.
	const previewUrl = `/api/invoice-print-templates/${templateId}/preview?v=${encodeURIComponent(template.updated_at ?? "")}${
		previewInvoiceRequestId ? `&invoice_request_id=${previewInvoiceRequestId}` : ""
	}`;

	return (
		<Content pageName="Invoice Print Template">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<Link to="/invoice-print-templates" className="inline-flex items-center gap-1 text-sm text-foreground/70 hover:text-foreground-dark w-fit">
					<IconArrowLeft size={16} stroke={2} />
					Back to templates
				</Link>

				<div className="flex flex-row items-start justify-between w-full gap-4 flex-wrap">
					<div className="flex flex-col gap-2 max-w-md w-full">
						<Input
							className="input !text-xl !font-semibold !h-auto !py-1"
							value={draft.name}
							onChange={(e) => setDraft((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
						/>
						<span
							className={clsx(
								"w-fit px-2 py-1 rounded-full text-xs font-medium",
								template.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800",
							)}
						>
							{template.is_active ? "Active" : "Inactive"}
						</span>
					</div>
					<div className="flex flex-row items-center gap-2 flex-wrap">
						{!template.is_active && (
							<Button variant="outline" onClick={handleActivate} disabled={activateMutation.isPending}>
								<IconCheck size={16} stroke={2} />
								Activate
							</Button>
						)}
						<Button
							variant="danger"
							onClick={handleDelete}
							disabled={template.is_active}
							title={template.is_active ? "Activate another template first" : undefined}
						>
							<IconTrash size={16} stroke={2} />
							Delete
						</Button>
						<Button onClick={handleSave} disabled={updateMutation.isPending}>
							{updateMutation.isPending ? "Saving..." : "Save Changes"}
						</Button>
					</div>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-[1fr_520px] gap-6 w-full items-start">
					{/* Controls */}
					<div className="flex flex-col gap-4 w-full">
						<Section title="Page">
							<div className="grid grid-cols-2 gap-4">
								<LabeledControl label="Page size">
									<Select
										options={PAGE_SIZE_OPTIONS}
										defaultValue={draft.page_size}
										clearable={false}
										onValueChange={(v) => setDraft((prev) => (prev ? { ...prev, page_size: v as PageSize } : prev))}
									/>
								</LabeledControl>
								<LabeledControl label="Orientation">
									<Select
										options={ORIENTATION_OPTIONS}
										defaultValue={draft.orientation}
										clearable={false}
										onValueChange={(v) => setDraft((prev) => (prev ? { ...prev, orientation: v as Orientation } : prev))}
									/>
								</LabeledControl>
							</div>
							<div className="flex flex-col gap-2">
								<span className="text-sm font-medium text-foreground-dark">Page offset</span>
								<span className="text-xs text-foreground/70">
									Nudges every field, the item table, summary, and signatories at once — use this for a systematic drift instead of adjusting each position
									individually.
								</span>
								<div className="grid grid-cols-2 gap-2">
									<NumberInput label="Offset X" value={draft.settings.page.offset_x ?? 0} onChange={(v) => updatePage({ offset_x: v })} />
									<NumberInput label="Offset Y" value={draft.settings.page.offset_y ?? 0} onChange={(v) => updatePage({ offset_y: v })} />
								</div>
							</div>
							<div className="flex flex-col gap-2">
								<span className="text-sm font-medium text-foreground-dark">Background</span>
								<div className="flex items-center gap-3">
									{template.background_url && <img src={template.background_url} alt="" className="w-10 h-14 object-cover rounded border border-border/50" />}
									<input
										ref={fileInputRef}
										type="file"
										accept="image/png,image/jpeg,image/webp,application/pdf"
										className="hidden"
										onChange={handleBackgroundChange}
									/>
									<Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={backgroundMutation.isPending}>
										<IconUpload size={14} stroke={2} />
										{backgroundMutation.isPending ? "Uploading..." : "Replace Background"}
									</Button>
								</div>
							</div>
						</Section>

						<Section title="Fields">
							<div className="flex flex-col gap-4">
								{FIELD_KEYS.map((key) => {
									const field = draft.settings.fields[key] as FieldSetting;
									return (
										<div key={key} className="flex flex-col gap-2 pb-3 border-b border-border/30 last:border-b-0 last:pb-0">
											<span className="text-sm font-medium text-foreground-dark">{FIELD_LABELS[key]}</span>
											<div className="grid grid-cols-4 gap-2">
												<NumberInput label="X" value={field.x} onChange={(v) => updateField(key, { x: v })} />
												<NumberInput label="Y" value={field.y} onChange={(v) => updateField(key, { y: v })} />
												<NumberInput label="Width" value={field.width} onChange={(v) => updateField(key, { width: v })} />
												<NumberInput label="Font size" value={field.font_size} step={0.5} onChange={(v) => updateField(key, { font_size: v })} />
											</div>
											<div className="flex items-center gap-4">
												<AlignSelect value={field.align ?? "left"} onChange={(v) => updateField(key, { align: v })} />
												<label htmlFor={`field-bold-${key}`} className="flex items-center gap-2 text-sm text-foreground-dark cursor-pointer">
													<Checkbox
														id={`field-bold-${key}`}
														checked={!!field.bold}
														onCheckedChange={(checked) => updateField(key, { bold: checked === true })}
													/>
													Bold
												</label>
											</div>
										</div>
									);
								})}
							</div>
						</Section>

						<Section title="Item Table">
							<div className="grid grid-cols-3 gap-2">
								<NumberInput label="X" value={draft.settings.items.x} onChange={(v) => updateItems({ x: v })} />
								<NumberInput label="Y" value={draft.settings.items.y} onChange={(v) => updateItems({ y: v })} />
								<NumberInput label="Width" value={draft.settings.items.width} onChange={(v) => updateItems({ width: v })} />
								<NumberInput label="Row height" value={draft.settings.items.row_height} step={0.1} onChange={(v) => updateItems({ row_height: v })} />
								<NumberInput label="Leading rows" value={draft.settings.items.leading_rows} onChange={(v) => updateItems({ leading_rows: v })} />
								<NumberInput label="Max rows" value={draft.settings.items.max_rows} onChange={(v) => updateItems({ max_rows: v })} />
								<NumberInput label="Font size" value={draft.settings.items.font_size} step={0.5} onChange={(v) => updateItems({ font_size: v })} />
							</div>
							<div className="flex flex-col gap-2">
								<span className="text-sm font-medium text-foreground-dark">Columns (width is % of table width)</span>
								{ITEM_COLUMN_KEYS.map((key) => {
									const column = draft.settings.items.columns[key] as ItemColumnSetting;
									return (
										<div key={key} className="flex items-center gap-3">
											<span className="text-sm text-foreground/70 w-28 shrink-0">{ITEM_COLUMN_LABELS[key]}</span>
											<NumberInput label="Width %" value={column.width} onChange={(v) => updateItemColumn(key, { width: v })} />
											<AlignSelect value={column.align} onChange={(v) => updateItemColumn(key, { align: v })} />
										</div>
									);
								})}
							</div>
						</Section>

						<Section title="Summary">
							<div className="grid grid-cols-4 gap-2">
								<NumberInput label="X" value={draft.settings.summary.x} onChange={(v) => updateSummary({ x: v })} />
								<NumberInput label="Y" value={draft.settings.summary.y} onChange={(v) => updateSummary({ y: v })} />
								<NumberInput label="Width" value={draft.settings.summary.width} onChange={(v) => updateSummary({ width: v })} />
								<NumberInput label="Row height" value={draft.settings.summary.row_height} step={0.1} onChange={(v) => updateSummary({ row_height: v })} />
								<NumberInput label="Font size" value={draft.settings.summary.font_size} step={0.5} onChange={(v) => updateSummary({ font_size: v })} />
							</div>
						</Section>

						<Section title="Signatories">
							<div className="flex flex-col gap-3">
								{draft.settings.signatories.map((signatory, index) => (
									<div key={index} className="flex flex-col gap-2 pb-3 border-b border-border/30 last:border-b-0 last:pb-0">
										<div className="flex items-center gap-2">
											<Input
												className="input"
												value={signatory.name}
												onChange={(e) => updateSignatory(index, { name: e.target.value })}
												placeholder="Signatory name"
											/>
											<Button type="button" variant="ghost" size="icon" onClick={() => removeSignatory(index)} title="Remove signatory">
												<IconTrash size={16} stroke={2} />
											</Button>
										</div>
										<div className="grid grid-cols-4 gap-2">
											<NumberInput label="X" value={signatory.x} onChange={(v) => updateSignatory(index, { x: v })} />
											<NumberInput label="Y" value={signatory.y} onChange={(v) => updateSignatory(index, { y: v })} />
											<NumberInput label="Width" value={signatory.width} onChange={(v) => updateSignatory(index, { width: v })} />
											<NumberInput label="Font size" value={signatory.font_size ?? 9.5} step={0.5} onChange={(v) => updateSignatory(index, { font_size: v })} />
										</div>
									</div>
								))}
								<Button type="button" variant="outline" size="sm" className="w-fit" onClick={addSignatory}>
									<IconPlus size={14} stroke={2} />
									Add Signatory
								</Button>
							</div>
						</Section>
					</div>

					{/* Preview */}
					<div className="flex flex-col gap-3 lg:sticky lg:top-6">
						<div className="flex flex-col gap-1">
							<span className="text-sm font-medium text-foreground-dark">Live draft preview</span>
							<span className="text-xs text-foreground/70">
								Updates as you edit, but it's an HTML approximation — fonts and text wrapping can differ slightly from the real PDF. Use the actual print
								preview below to confirm.
							</span>
						</div>
						<PreviewCanvas settings={draft.settings} backgroundUrl={template.background_url} scale={scale} />

						<div className="flex flex-col gap-2 p-4 rounded-md border border-border/50 bg-white">
							<span className="text-sm font-medium text-foreground-dark">Actual print preview</span>
							<span className="text-xs text-foreground/70">The real generated PDF — reflects the last saved settings.</span>
							<Select options={invoiceRequestOptions} placeholder="Sample data" onValueChange={(v) => setPreviewInvoiceRequestId((v as string) || null)} />
							<iframe src={previewUrl} title="Actual print preview" className="w-full h-[520px] rounded-md border border-border/50" />
							<Button type="button" variant="outline" onClick={handlePreview}>
								<IconFileTypePdf size={16} stroke={2} />
								Open in new tab
							</Button>
						</div>
					</div>
				</div>
			</div>
		</Content>
	);
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<div className="flex flex-col gap-4 p-4 rounded-md border border-border/50 bg-white">
			<span className="text-base font-semibold text-foreground-dark">{title}</span>
			{children}
		</div>
	);
}

function LabeledControl({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-sm font-medium text-foreground-dark">{label}</span>
			{children}
		</div>
	);
}

function NumberInput({ label, value, onChange, step = 1 }: { label: string; value: number; onChange: (value: number) => void; step?: number }) {
	return (
		<label className="flex flex-col gap-1 text-xs text-foreground/70">
			{label}
			<input
				type="number"
				step={step}
				value={value}
				onChange={(e) => onChange(Number(e.target.value))}
				className="w-full h-8 px-2 rounded border border-border text-sm text-foreground-dark focus:border-focus-border focus:ring-2 focus:ring-focus-ring/30 outline-0"
			/>
		</label>
	);
}

function AlignSelect({ value, onChange }: { value: FieldAlign; onChange: (value: FieldAlign) => void }) {
	return (
		<select
			value={value}
			onChange={(e) => onChange(e.target.value as FieldAlign)}
			className="h-8 px-2 rounded border border-border text-sm text-foreground-dark"
		>
			{ALIGN_OPTIONS.map((option) => (
				<option key={option} value={option}>
					{option[0]?.toUpperCase()}
					{option.slice(1)}
				</option>
			))}
		</select>
	);
}

function PreviewCanvas({ settings, backgroundUrl, scale }: { settings: PrintTemplateSettings; backgroundUrl: string | null; scale: number }) {
	const { page } = settings;
	const offsetX = page.offset_x ?? 0;
	const offsetY = page.offset_y ?? 0;
	const containerStyle = useMemo(() => ({ width: page.width * scale, height: page.height * scale }), [page.width, page.height, scale]);
	const pageStyle = useMemo(
		() => ({
			width: page.width,
			height: page.height,
			transform: `scale(${scale})`,
			transformOrigin: "top left" as const,
			fontFamily: "Arial, Helvetica, sans-serif",
		}),
		[page.width, page.height, scale],
	);

	const items = settings.items;
	const shownItems = SAMPLE_ITEMS.slice(0, items.max_rows);
	// Mirrors the blade loop exactly: leading blank rows, the item rows, one
	// "Nothing Follows" row (always the 3rd column), then blank filler rows
	// up to max_rows — so the preview's table height matches the real PDF's.
	const itemRows: Array<{ kind: "blank" | "item" | "nothing-follows"; item?: Record<ItemColumnKey, string> }> = [
		...Array.from({ length: items.leading_rows }, () => ({ kind: "blank" as const })),
		...shownItems.map((item) => ({ kind: "item" as const, item })),
		{ kind: "nothing-follows" as const },
		...Array.from({ length: Math.max(0, items.max_rows - shownItems.length - 1) }, () => ({ kind: "blank" as const })),
	];

	return (
		<div style={containerStyle} className="relative border border-border/50 shadow-sm bg-white overflow-hidden shrink-0">
			<div style={pageStyle} className="relative">
				{backgroundUrl && (
					<img src={backgroundUrl} alt="Template background" className="absolute inset-0 w-full h-full object-fill pointer-events-none select-none" />
				)}
				{FIELD_KEYS.map((key) => {
					const field = settings.fields[key] as FieldSetting;
					return (
						<div
							key={key}
							className={clsx("absolute break-words", field.bold && "font-bold")}
							style={{
								left: field.x + offsetX,
								top: field.y + offsetY,
								width: field.width,
								fontSize: field.font_size,
								lineHeight: 1.2,
								textAlign: field.align ?? "left",
							}}
						>
							{SAMPLE_FIELD_TEXT[key]}
						</div>
					);
				})}

				<div
					className="absolute border border-dashed border-blue-400/70"
					style={{ left: items.x + offsetX, top: items.y + offsetY, width: items.width, fontSize: items.font_size, boxSizing: "border-box" }}
				>
					{itemRows.map((row, rowIndex) => (
						<div key={rowIndex} className="flex border-b border-dashed border-blue-400/30 last:border-b-0" style={{ minHeight: items.row_height }}>
							{ITEM_COLUMN_KEYS.map((key, columnIndex) => {
								const column = items.columns[key] as ItemColumnSetting;
								return (
									<div
										key={key}
										className={clsx(
											"shrink grow-0 min-w-0 break-words border-r border-dashed border-blue-400/40 last:border-r-0 box-border",
											row.kind === "nothing-follows" && columnIndex === 2 && "italic text-center",
										)}
										style={{ width: `${column.width}%`, textAlign: column.align, lineHeight: `${items.row_height}px`, padding: "0 3px" }}
									>
										{row.kind === "item" && row.item ? row.item[key] : row.kind === "nothing-follows" && columnIndex === 2 ? "Nothing Follows" : ""}
									</div>
								);
							})}
						</div>
					))}
				</div>

				<div
					className="absolute"
					style={{
						left: settings.summary.x + offsetX,
						top: settings.summary.y + offsetY,
						width: settings.summary.width,
						fontSize: settings.summary.font_size,
					}}
				>
					{Array.from({ length: 7 }).map((_, index) => (
						<div key={index} className="text-right" style={{ height: settings.summary.row_height, lineHeight: `${settings.summary.row_height}px` }}>
							0.00
						</div>
					))}
				</div>

				{settings.signatories.map((signatory, index) => (
					<div
						key={index}
						className="absolute text-center font-bold"
						style={{ left: signatory.x + offsetX, top: signatory.y + offsetY, width: signatory.width, fontSize: signatory.font_size ?? 9.5 }}
					>
						{signatory.name}
					</div>
				))}
			</div>
		</div>
	);
}
